import type {
	CoreApiModel,
	CoreApiPaginatedExtendedResponse,
	CoreApiUuidModel,
	PaginatedParams,
} from '../common.ts';
import type { PriceCurrency } from '../constants.ts';
import type { MyLQAInvoiceCollection } from './invoice.ts';
import type { MyLQATierVariantCollection } from './product.ts';
import type { LqaUser } from '../../queries';
import type { IChange } from 'json-diff-ts';

export type MyLQAContractBillingInterval = 'on_renewal' | 'monthly';
export const myLQAContractBillingIntervalValues = [
	'on_renewal',
	'monthly',
] as const;

export const myLQAContractBillingIntervalValuesDisplayNames: Record<
	MyLQAContractBillingInterval,
	string
> = {
	on_renewal: 'Yearly',
	monthly: 'Monthly',
};

export type MyLQAPaymentProvider =
	| 'stripe'
	| 'stripe_subscriptions'
	| 'internal_billing'
	| 'internal_trial'
	| 'stripe_invoicing';
export const myLQAPaymentProviderValues = [
	'stripe',
	'stripe_subscriptions',
	'internal_billing',
	'internal_trial',
	'stripe_invoicing',
] as const;

export const myLQAPaymentProviderValuesDisplayNames: Record<
	MyLQAPaymentProvider,
	string
> = {
	stripe: 'Stripe',
	stripe_subscriptions: 'Recurring payments',
	stripe_invoicing: 'Non-recurring payments',
	internal_billing: 'Internal Billing',
	internal_trial: 'Trial',
};

export type MyLQAContractState =
	'created' | 'active' | 'unpaid' | 'deactivated';
export const myLQAContractStateValues = [
	'created',
	'active',
	'unpaid',
	'deactivated',
] as const;

export type MyLQAInvoiceState = 'generated' | 'sent' | 'paid' | 'void';

export interface MyLQAContractPaymentProviderMetadataStripeEnriched {
	id: string;
	paymentMethod?: {
		type: 'card' | string;
		card?: {
			brand: string;
			last4: string;
			expiryMonth: number;
			expiryYear: number;
		};
	};

	[key: string]: unknown;
}

export interface MyLQAContractCollection extends CoreApiUuidModel {
	startDate: Date;
	endDate?: Date;
	autoRenewal: boolean;
	paymentProvider: MyLQAPaymentProvider;
	state: MyLQAContractState;
	billingInterval: MyLQAContractBillingInterval;
	paymentProviderMetadata: Record<string, unknown>;
	licensedHotels?: {
		id: number;
		name: string;
		isActive?: boolean;
	}[];
	subscriptionsLimit?: number | null;
	fixedPrice?: number | null;
	currency: PriceCurrency;
	tierVariant: MyLQATierVariantCollection;
	ownedByBrand?: { id: number; name: string };
	ownedByHotel?: { id: number; name: string };
	billingAgent: {
		id: number;
		userFirstName: string;
		userLastName: string;
		email?: string;
	} | null;
	pastPeriod?: MyLQAContractPeriodCollection;
	currentPeriod?: MyLQAContractPeriodCollection;
	upcomingPeriod?: MyLQAContractPeriodCollection;
	latestInvoice: MyLQAInvoiceCollection;
	notes?: string;
	email?: string | null;
}

export interface MyLQAContract extends MyLQAContractCollection {
	paymentProviderMetadata:
		| Record<string, unknown>
		| MyLQAContractPaymentProviderMetadataStripeEnriched;
}

export interface MyLQAContractsStatistics {
	totalActiveContracts: number;
	totalTrialHotels: number;
	totalSeatsActive: number;
	totalSeatsUsed: number;
}

export enum MyLQALicensedHotelStatusEnum {
	CurrentContract = 'current_contract',
	NoContract = 'no_contract',
	AlternativeContract = 'alternative_contract',
}

export interface IMyLQALicensedHotelContract {
	id: string;
	brandId: number;
	brandName: string;
}

export interface IMyLQALicensedHotelMembership {
	status: MyLQALicensedHotelStatusEnum;
	contract: IMyLQALicensedHotelContract | null;
}

export interface IMyLQALicensedHotel extends CoreApiModel {
	hotelId: number;
	hotelName: string;
	status: MyLQALicensedHotelStatusEnum;
	contract: IMyLQALicensedHotelContract | null;
	seats: number | null;
}

export interface IMyLQALicensedHotelCounts {
	included: number;
	excluded: number;
}

export interface IMyLQALicensedHotelsResponse
	extends
		CoreApiPaginatedExtendedResponse<IMyLQALicensedHotel>,
		IMyLQALicensedHotelCounts {}

export interface IMyLQALicensedHotelsParams extends Omit<
	PaginatedParams,
	'sort'
> {
	included: boolean;
	search?: string;
}

export interface MyLQAContractPeriodCollection extends CoreApiUuidModel {
	from: Date;
	to: Date;
	tierVariant: {
		id: string;
		tier: {
			id: string;
			name: string;
		};
		includedSeats: number;
		pricePerMonth: number;
	};
}

export interface MyLQAContractActivityLog extends CoreApiUuidModel {
	entityType: string;
	entityId: string;
	changes: IChange[];
	createdBy?: LqaUser;
}
